/**
 * Pinned scenarios for the calculator's comparison view (Phase C6).
 *
 * Smaller cousin of calc-recents: fewer entries (max 3), explicit
 * user action to add (Pin button), no dedupe-by-timestamp. Users
 * build a shootout of 2-3 options and compare them side by side.
 */

import type { UiValues } from './_shared';

const KEY = 'fsp.calc.pinned.v1';
export const MAX_PINNED = 3;

export type PinnedSummary = {
  label: string;
  perUnitSell: number;
  orderTotal: number;
  marginPct: number;
  totalQty: number;
  mode: 'b2c' | 'b2b';
};

export type PinnedScenario = {
  /** Stable id so React keys + remove() don't collide when two pins share a label. */
  id: string;
  at: number;
  values: UiValues;
  summary: PinnedSummary;
};

export function loadPinned(): PinnedScenario[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPinned).slice(0, MAX_PINNED);
  } catch {
    return [];
  }
}

/**
 * Append a pin. Evicts the oldest when at MAX_PINNED so users don't
 * have to unpin before adding. Matches Google Sheets' "compare"
 * affordance mental model: always room for one more, the stale one
 * falls off.
 */
export function addPinned(entry: Omit<PinnedScenario, 'id'>): PinnedScenario[] {
  const list = loadPinned();
  const id = `${entry.at}-${Math.random().toString(36).slice(2, 8)}`;
  const next = [...list, { ...entry, id }].slice(-MAX_PINNED);
  writeAll(next);
  return next;
}

export function removePinned(id: string): PinnedScenario[] {
  const list = loadPinned().filter((p) => p.id !== id);
  writeAll(list);
  return list;
}

export function clearPinned(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // noop
  }
}

function writeAll(list: PinnedScenario[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Storage unavailable — pin session stays in-memory until reload.
  }
}

function isPinned(v: unknown): v is PinnedScenario {
  if (!v || typeof v !== 'object') return false;
  const p = v as PinnedScenario;
  return (
    typeof p.id === 'string' &&
    typeof p.at === 'number' &&
    !!p.values &&
    !!p.summary &&
    typeof p.summary.label === 'string' &&
    typeof p.summary.perUnitSell === 'number'
  );
}
